/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2025-07-09 17:26:36
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-07-14 17:52:03
 */
import { useState, useEffect } from 'react'

/**
 * 自定义Hook，用于处理表格大小调整和滚动位置
 * @param {number} offsetHeight - 表格距离窗口顶部的偏移量，默认为380
 * @returns {number} - 计算后的表格高度
 */
export default function useTableResizer(offsetHeight = 380): number {
  const [tableHeight, setTableHeight] = useState(
    window.innerHeight - offsetHeight
  )

  useEffect(() => {
    const handleResize = () => {
      // 计算表格高度
      const calculatedHeight = window.innerHeight - offsetHeight
      setTableHeight(calculatedHeight)

      // 强制表格重新计算布局
      const tableBody = document.querySelector('.ant-table-body') as HTMLElement | null
      if (tableBody) {
        tableBody.style.maxHeight = `${calculatedHeight}px`
      }
    }

    // 处理固定列与内容不对齐问题
    const fixAlignmentIssue = () => {
      // 获取表格容器
      const tableBody = document.querySelector('.ant-table-body') as HTMLElement | null
      const fixedLeftColumns = document.querySelector(
        '.ant-table-fixed-left .ant-table-body-inner'
      ) as HTMLElement | null
      const fixedRightColumns = document.querySelector(
        '.ant-table-fixed-right .ant-table-body-inner'
      ) as HTMLElement | null

      if (!tableBody) return

      // 监听表格滚动事件
      tableBody.addEventListener('scroll', () => {
        // 调整固定列阴影
        const leftFixedEl = document.querySelector('.ant-table-fixed-left')
        const rightFixedEl = document.querySelector('.ant-table-fixed-right')

        if (tableBody.scrollLeft > 0) {
          if (leftFixedEl)
            leftFixedEl.classList.add('ant-table-fixed-left-shadow')
        } else {
          if (leftFixedEl)
            leftFixedEl.classList.remove('ant-table-fixed-left-shadow')
        }

        if (
          tableBody.scrollLeft + tableBody.clientWidth <
          tableBody.scrollWidth
        ) {
          if (rightFixedEl)
            rightFixedEl.classList.add('ant-table-fixed-right-shadow')
        } else {
          if (rightFixedEl)
            rightFixedEl.classList.remove('ant-table-fixed-right-shadow')
        }

        // 当滚动到底部时，强制同步高度
        if (
          tableBody.scrollHeight - tableBody.scrollTop <=
          tableBody.clientHeight + 1
        ) {
          // 为固定列底部填充滚动条高度
          const scrollbarHeight =
            tableBody.offsetHeight - tableBody.clientHeight

          if (fixedLeftColumns) {
            fixedLeftColumns.style.paddingBottom = `${scrollbarHeight}px`
            fixedLeftColumns.style.boxSizing = 'border-box'
          }

          if (fixedRightColumns) {
            fixedRightColumns.style.paddingBottom = `${scrollbarHeight}px`
            fixedRightColumns.style.boxSizing = 'border-box'
          }
        }
      })

      // 添加全局样式以修复对齐问题
      const styleTag = document.createElement('style')
      styleTag.id = 'table-alignment-fix'
      styleTag.innerHTML = `
        .ant-table-fixed-right,
        .ant-table-fixed-left {
          overflow: hidden;
        }
        .ant-table-scroll .ant-table-header {
          margin-bottom: 0 !important;
          padding-bottom: 0 !important;
          overflow: hidden !important;
        }
        .ant-table-header-column {
          display: inline-block;
        }
      `

      // 如果不存在此样式，添加到文档头部
      if (!document.getElementById('table-alignment-fix')) {
        document.head.appendChild(styleTag)
      }
    }

    window.addEventListener('resize', handleResize)
    // 初始调用一次确保布局正确
    handleResize()

    // 在组件加载后延迟执行，确保表格完全渲染
    const timer = setTimeout(() => {
      // 触发窗口resize事件以强制表格重新计算布局
      window.dispatchEvent(new Event('resize'))

      // 强制刷新滚动条
      const tableContainer = document.querySelector('.ant-table-body') as HTMLElement | null
      if (tableContainer) {
        // 临时改变scroll位置再还原，强制重新计算
        const scrollLeft = tableContainer.scrollLeft
        tableContainer.scrollLeft = scrollLeft + 1
        setTimeout(() => {
          tableContainer.scrollLeft = scrollLeft
        }, 0)
      }

      // 修复对齐问题
      fixAlignmentIssue()
    }, 500)

    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(timer)
    }
  }, [offsetHeight])

  return tableHeight
}
